import React, { useState, useCallback, useEffect, useMemo } from 'react';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import ru from 'date-fns/locale/ru';
import 'react-datepicker/dist/react-datepicker.css';
import '../../../styles/DatePicker.css';
import { marked } from 'marked';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button,
    TextField,
    Box,
    Typography,
    IconButton,
    Grid,
    Autocomplete,
    Chip,
    useMediaQuery,
    useTheme,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Checkbox,
    FormControlLabel,
    Tooltip,
    Menu,
    MenuItem,
    Popper,
} from '@mui/material';
import {
    Close as CloseIcon,
    Add as AddIcon,
    Edit as EditIcon,
    CalendarMonth as CalendarIcon,
    Person as PersonIcon,
    Business as BusinessIcon,
    Build as BuildIcon,
    Save as SaveIcon,
    Folder as FolderIcon,
    Warning as WarningIcon,
    CheckCircle as CheckCircleIcon,
    AttachMoney as AttachMoneyIcon,
    Comment as CommentIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Delete as DeleteIcon,
    KeyboardArrowDown as KeyboardArrowDownIcon,
    KeyboardArrowUp as KeyboardArrowUpIcon,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { selectTransformers } from '../../../store/selectors/supervisionSelector';
import { $authHost } from '../../../http';
import { ktpIcon, projectStatusOreolIcon } from '../../layers/icons';

// Регистрируем русскую локаль
registerLocale('ru', ru);

// Дерево требований для определения доступных работ для каждой категории
const requirementsTree = {
    '48': [67, 72, 73, 74],
    '49': [66],
    '50': [68, 75],
    '51': [67, 69, 72, 73, 74],
    '52': [66],
    '53': [68, 70, 75],
    '55': [65],
    '61': [65, 71],
    '63': [65]
};

// Компонент для отображения markdown контента
const MarkdownContent = ({ content, darkMode }) => {
    if (!content) return null;

    const htmlContent = marked(content);

    return (
        <Box
            sx={{
                '& p': {
                    margin: '1px 0',
                    fontSize: '0.875rem',
                    lineHeight: '1.4',
                },
                '& strong': {
                    fontWeight: 'bold',
                },
                '& em': {
                    fontStyle: 'italic',
                },
                '& ul, & ol': {
                    margin: '1px 0',
                    paddingLeft: '8px',
                },
                '& li': {
                    margin: '0px 0',
                    fontSize: '0.875rem',
                },
                '& code': {
                    backgroundColor: darkMode ? '#333' : '#f5f5f5',
                    padding: '1px 2px',
                    borderRadius: '2px',
                    fontSize: '0.8rem',
                    fontFamily: 'monospace',
                },
                '& pre': {
                    backgroundColor: darkMode ? '#333' : '#f5f5f5',
                    padding: '2px',
                    borderRadius: '2px',
                    overflow: 'auto',
                    fontSize: '0.8rem',
                },
                '& blockquote': {
                    borderLeft: `2px solid ${darkMode ? '#2196f3' : '#1565c0'}`,
                    paddingLeft: '4px',
                    margin: '1px 0',
                    fontStyle: 'italic',
                },
                '& h1, & h2, & h3, & h4, & h5, & h6': {
                    margin: '1px 0',
                    fontWeight: 'bold',
                },
                '& h1': { fontSize: '1.1rem' },
                '& h2': { fontSize: '1rem' },
                '& h3': { fontSize: '0.95rem' },
                '& h4': { fontSize: '0.9rem' },
                '& h5': { fontSize: '0.85rem' },
                '& h6': { fontSize: '0.8rem' },
            }}
            dangerouslySetInnerHTML={{ __html: htmlContent }}
        />
    );
};

// Тестовые данные для autocomplete
const mockObjectNames = [
    'Объект 1 - Строительство ВЛ 10кВ',
    'Объект 2 - Реконструкция КТП',
    'Объект 3 - Монтаж оборудования',
    'Объект 4 - Прокладка кабеля',
    'Объект 5 - Установка опор',
];



const mockEquipment = [
    'КТП-1 10/0,4кВ',
    'КТП-2 10/0,4кВ',
    'ВЛ-10кВ',
    'Кабель СИП-3',
    'Опора железобетонная',
    'Трансформатор ТМ-160',
    'Выключатель ВВ/TEL',
    'Разъединитель РЛН',
];

const objectTypes = {
    'transformers': 'ТП 6(10)/0,4 кВ'
}



const ObjectForm = ({
    formData,
    darkMode,
    isEditing,
    setFormData,
    objects = [],
    stages = [],
    responsible = [],
    equipment = [],
    projects = [],
    selectedProjectId = null,
    sortedProjects = [],
    blockedProjects = [],
    isCheckingProjects = false,
    expandedCategories,
    setExpandedCategories,
    workMenuAnchor,
    setWorkMenuAnchor,
    selectedCategoryForWork,
    setSelectedCategoryForWork,
    handleAddCategoryClick,
    categoryMenuAnchor
}) => {

    const fieldColors = {
        name: { light: '#2e7d32', dark: '#4caf50' },
        project: { light: '#9c27b0', dark: '#ba68c8' },
        dates: { light: '#1976d2', dark: '#64b5f6' },
        stage: { light: '#6a1b9a', dark: '#ab47bc' },
        responsible: { light: '#d32f2f', dark: '#f44336' },
        coexecutors: { light: '#f57c00', dark: '#ffb74d' },
        equipment: { light: '#3949ab', dark: '#5c6bc0' },
        closing: { light: '#e65100', dark: '#ff8f00' },
        cost: { light: '#c2185b', dark: '#e91e63' },
        comment: { light: '#1565c0', dark: '#2196f3' },
    };


    // Функции для управления категориями и работами
    const toggleCategory = (categoryUuid) => {
        setExpandedCategories(prev => {
            const newSet = new Set(prev);
            if (newSet.has(categoryUuid)) {
                newSet.delete(categoryUuid);
            } else {
                newSet.add(categoryUuid);
            }
            return newSet;
        });
    };

    const handleAddWorkClick = (event, categoryUuid) => {
        event.stopPropagation();
        setWorkMenuAnchor(event.currentTarget);
        setSelectedCategoryForWork(categoryUuid);
    };

    const handleWorkMenuClose = () => {
        setWorkMenuAnchor(null);
        setSelectedCategoryForWork(null);
    };

    const handleAddWork = (workId) => {
        if (!selectedCategoryForWork) return;

        setFormData(prev => {
            const newCategories = [...(prev.categories || [])];
            const categoryIndex = newCategories.findIndex(cat => cat.uuid === selectedCategoryForWork);
            if (categoryIndex !== -1) {
                const existingWorks = newCategories[categoryIndex].works || [];
                if (!existingWorks.find(w => w.id === workId)) {
                    newCategories[categoryIndex] = {
                        ...newCategories[categoryIndex],
                        works: [...existingWorks, {
                            id: workId,
                            plan_start: null,
                            fact_start: null,
                            plan_end: null,
                            fact_end: null,
                            stage: null,
                            responsible: null,
                            coexecutors: null,
                            closing: null,
                            cost: null,
                            comment: null,
                            name_by_acts: null
                        }]
                    };
                }
            }
            return { ...prev, categories: newCategories };
        });
        handleWorkMenuClose();
    };

    const handleDeleteWork = (categoryIndex, workIndex) => {
        setFormData(prev => {
            const newCategories = [...(prev.categories || [])];
            if (newCategories[categoryIndex]) {
                const works = [...(newCategories[categoryIndex].works || [])];
                works.splice(workIndex, 1);
                newCategories[categoryIndex] = {
                    ...newCategories[categoryIndex],
                    works
                };
            }
            return { ...prev, categories: newCategories };
        });
    };

    const handleUpdateWork = (categoryIndex, workIndex, field, value) => {
        setFormData(prev => {
            const newCategories = [...(prev.categories || [])];
            if (newCategories[categoryIndex] && newCategories[categoryIndex].works) {
                const works = [...newCategories[categoryIndex].works];
                works[workIndex] = { ...works[workIndex], [field]: value };
                newCategories[categoryIndex] = {
                    ...newCategories[categoryIndex],
                    works
                };
            }
            return { ...prev, categories: newCategories };
        });
    };

    // Функция для форматирования даты
    const formatDate = (dateStr, short = false) => {
        if (!dateStr) return '';
        try {
            const date = new Date(dateStr);
            if (isNaN(date.getTime())) return '';
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            if (short) {
                return `${day}.${month}.${year}`;
            }
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            return `${day}.${month}.${year} ${hours}:${minutes}`;
        } catch {
            return '';
        }
    };

    // Функция переключения режима поля
    const toggleFieldMode = (fieldName) => {
        if (Array.isArray(formData[fieldName])) {
            if (fieldName === 'coexecutors') {
                setFormData(prev => ({
                    ...prev,
                    [fieldName]: Array.isArray(prev[fieldName][0]) ? prev[fieldName][0] : [[...prev[fieldName]], ...Array.from({ length: prev.equipment.length - 1 }, () => ([...prev[fieldName]]))]
                }))

            } else {
                setFormData(prev => ({
                    ...prev,
                    [fieldName]: prev[fieldName][0]
                }))
            }
        } else {
            setFormData(prev => ({
                ...prev,
                [fieldName]: [prev[fieldName], ...Array.from({ length: prev.equipment.length - 1 }, () => prev[fieldName])]
            }))
        }
    };

    // Получаем данные категорий для отображения
    const categoriesData = formData.categories || [];

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            pt: 2,
            gap: 1,
            position: 'relative',
            zIndex: 1,
            '& .MuiTextField-root': {
                '& .MuiOutlinedInput-root': {
                    bgcolor: darkMode ? '#3a3a3a' : '#fff',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    position: 'relative',
                    zIndex: 1,
                    '& input, & textarea': {
                        cursor: 'text !important',
                        pointerEvents: 'auto',
                        zIndex: 2,
                        color: darkMode ? '#ffffff' : '#333'
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.23)'
                    },
                    '&:hover': {
                        bgcolor: darkMode ? '#424242' : '#fafafa',
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.4)'
                        }
                    },
                    '&.Mui-focused': {
                        bgcolor: darkMode ? '#424242' : '#fff',
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderWidth: '2px',
                        }
                    },
                    '&.Mui-error': {
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: darkMode ? '#f44336' : '#d32f2f'
                        }
                    },
                    // Стили для кнопок и иконок в TextField
                    '& .MuiInputAdornment-root': {
                        color: darkMode ? '#e0e0e0' : '#666'
                    },
                    '& .MuiIconButton-root': {
                        color: darkMode ? '#e0e0e0' : '#666',
                        '&:hover': {
                            bgcolor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.04)'
                        }
                    }
                },
                '& .MuiInputLabel-root': {
                    color: darkMode ? '#e0e0e0' : '#666',
                    fontWeight: 500,
                    '&.Mui-focused': {
                        fontWeight: 600,
                        color: darkMode ? '#ffffff' : '#1976d2'
                    }
                },
                '& .MuiFormHelperText-root': {
                    color: darkMode ? '#f44336' : '#d32f2f'
                }
            },
            // Стили для Autocomplete кнопок и иконок
            '& .MuiAutocomplete-root': {
                '& .MuiAutocomplete-clearIndicator': {
                    color: darkMode ? '#e0e0e0' : '#666',
                    '&:hover': {
                        color: darkMode ? '#ffffff' : '#333',
                        bgcolor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.04)'
                    }
                },
                '& .MuiAutocomplete-popupIndicator': {
                    color: darkMode ? '#e0e0e0' : '#666',
                    '&:hover': {
                        color: darkMode ? '#ffffff' : '#333',
                        bgcolor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.04)'
                    }
                },
                '& .MuiAutocomplete-endAdornment': {
                    color: darkMode ? '#e0e0e0' : '#666'
                }
            }
        }}>
            {/* Наименование объекта */}
            <Box>
                <Typography variant="subtitle2" sx={{
                    mb: 1,
                    fontWeight: 600,
                    color: darkMode ? fieldColors.name.dark : fieldColors.name.light,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                }}>
                    <BusinessIcon sx={{ fontSize: '16px' }} />
                    Наименование *
                </Typography>
                <Autocomplete
                    fullWidth
                    disabled={isEditing}
                    options={objects}
                    groupBy={(option) => objectTypes[option.type] || 'Неизвестный тип'}
                    getOptionLabel={(option) => `${option.name}${option.nickname ? ' ' + option.nickname : ''}${option.town ? ' ' + option.town : ''}`}
                    isOptionEqualToValue={(option, value) => option.fid === value.fid}
                    value={formData.object || null}
                    onChange={(event, newValue) => {
                        setFormData(prev => {
                            return {
                                ...prev,
                                object: newValue || null,
                            };
                        });
                    }}
                    filterOptions={(options, { inputValue }) => {
                        if (!inputValue) return options;

                        const searchTerms = inputValue.toLowerCase().split(' ').filter(term => term.length > 0);

                        return options.filter(option => {
                            const optionText = `${option.name}${option.nickname ? ' ' + option.nickname : ''}${option.town ? ' ' + option.town : ''}`.toLowerCase();

                            return searchTerms.every(term => optionText.includes(term));
                        });
                    }}
                    freeSolo
                    renderOption={(props, option) => (
                        <Box component="li" {...props} key={option.id || option.fid} sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            gap: 1,
                            alignItems: 'center',
                            color: darkMode ? '#ffffff' : '#333',
                            '&:hover': {
                                bgcolor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.04)'
                            }
                        }}>
                            <Box sx={{ position: 'relative', width: 32, height: 32 }}>
                                <Box style={{ position: 'absolute', margin: '5px 0 0 6px' }}>
                                    {ktpIcon('default', 20, 20)}
                                </Box>
                                {['2', '3', '4', '7'].includes(option?.layer) ? (
                                    <Box style={{ position: 'absolute', margin: '0 0 0 0' }}>
                                        {projectStatusOreolIcon('ktp', Number(option?.layer), 32)}
                                    </Box>
                                ) : null}
                            </Box>
                            <Typography sx={{
                                color: darkMode ? '#ffffff' : '#333',
                            }}>
                                {`${option.name}${option.nickname ? ' ' + option.nickname : ''}${option.town ? ' ' + option.town : ''}`}
                            </Typography>
                        </Box>
                    )}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder={"Выберите или введите наименование объекта"}
                            required
                            error={!formData.object?.name?.trim()}
                            sx={{
                                '& .MuiInputBase-root': {
                                    p: '0px 10px',
                                },
                                '& .MuiOutlinedInput-input': {
                                    p: 1,
                                    cursor: 'text',
                                    pointerEvents: 'auto',
                                    '&::placeholder': {
                                        color: darkMode ? '#999' : '#666',
                                        opacity: 1
                                    }
                                },
                                '& .MuiOutlinedInput-root.Mui-disabled': {
                                    bgcolor: darkMode ? '#2a2a2a' : '#f5f5f5',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'
                                    },
                                    '& .MuiOutlinedInput-input': {
                                        color: darkMode ? '#666' : '#999',
                                        cursor: 'not-allowed',
                                        '-webkit-text-fill-color': darkMode ? '#666' : '#999'
                                    }
                                },
                                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: darkMode ? fieldColors.name.dark : fieldColors.name.light,
                                }
                            }}
                        />
                    )}
                    slotProps={{
                        popper: {
                            sx: {
                                zIndex: 13000,
                                '& .MuiAutocomplete-paper': {
                                    bgcolor: darkMode ? '#2b2b2b' : '#fff',
                                    border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.12)',
                                    '& .MuiAutocomplete-option': {
                                        color: darkMode ? '#f5f7fa' : '#333',
                                        '&:hover': {
                                            bgcolor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
                                        }
                                    },
                                    '& .MuiAutocomplete-groupLabel': {
                                        bgcolor: darkMode ? '#1e1e1e' : '#f5f5f5',
                                        color: darkMode ? '#ffffff' : '#333',
                                        fontWeight: 700,
                                        fontSize: '1rem',
                                        padding: '0px 16px',
                                        borderBottom: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.12)'
                                    }
                                }
                            }
                        }
                    }}
                />
            </Box>



            {/* Проект */}
            <Box>
                <Typography variant="subtitle2" sx={{
                    mb: 1,
                    fontWeight: 600,
                    color: darkMode ? fieldColors.project.dark : fieldColors.project.light,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                }}>
                    <FolderIcon sx={{ fontSize: '16px' }} />
                    Проект
                    {isCheckingProjects && (
                        <Typography variant="caption" sx={{ ml: 1, color: darkMode ? '#999' : '#666' }}>
                            (проверка...)
                        </Typography>
                    )}
                </Typography>
                <Autocomplete
                    fullWidth
                    options={sortedProjects}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    value={formData.project || null}
                    error={!formData.project}
                    disableClearable
                    onChange={(event, newValue) => {
                        // Проверяем, что выбранный проект не заблокирован
                        const isBlocked = blockedProjects.some(p => p.id === newValue?.id);
                        if (isBlocked) {
                            return; // Не позволяем выбирать заблокированные проекты
                        }
                        setFormData(prev => ({
                            ...prev,
                            project: newValue || null
                        }));
                    }}
                    filterOptions={(options, { inputValue }) => {
                        if (!inputValue) return options;

                        const searchTerms = inputValue.toLowerCase().split(' ').filter(term => term.length > 0);

                        return options.filter(option => {
                            const optionText = option.name.toLowerCase();

                            return searchTerms.every(term => optionText.includes(term));
                        });
                    }}
                    renderOption={(props, option) => {
                        const isBlocked = blockedProjects.some(p => p.id === option.id);
                        return (
                            <Box component="li" {...props} sx={{
                                color: isBlocked ? (darkMode ? '#666' : '#999') : (darkMode ? '#ffffff' : '#333'),
                                opacity: isBlocked ? 0.6 : 1,
                                cursor: isBlocked ? 'not-allowed' : 'pointer',
                                '&:hover': {
                                    bgcolor: isBlocked ? 'transparent' : (darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.04)')
                                }
                            }}>
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    width: '100%'
                                }}>
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1
                                    }}>
                                        <Typography sx={{
                                            color: isBlocked ? (darkMode ? '#666' : '#999') : (darkMode ? '#ffffff' : '#333'),
                                            textDecoration: isBlocked ? 'line-through' : 'none'
                                        }}>
                                            {option.name}
                                        </Typography>
                                        {isBlocked && (
                                            <Typography variant="caption" sx={{
                                                color: darkMode ? '#ff6b6b' : '#f44336',
                                                fontSize: '0.75rem',
                                                fontWeight: 500
                                            }}>
                                                (объект уже существует)
                                            </Typography>
                                        )}
                                    </Box>
                                    {option.description && (
                                        <Typography variant="caption" sx={{
                                            color: isBlocked ? (darkMode ? '#555' : '#777') : (darkMode ? '#cccccc' : 'text.secondary')
                                        }}>
                                            {option.description}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        );
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder="Выберите проект"
                            sx={{
                                '& .MuiInputBase-root': {
                                    p: '0px 10px',
                                },
                                '& .MuiOutlinedInput-input': {
                                    p: 1,
                                    cursor: 'text',
                                    pointerEvents: 'auto',
                                    '&::placeholder': {
                                        color: darkMode ? '#999' : '#666',
                                        opacity: 1
                                    }
                                },
                                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: darkMode ? fieldColors.project.dark : fieldColors.project.light,
                                }
                            }}
                        />
                    )}
                    slotProps={{
                        popper: {
                            sx: {
                                zIndex: 13000,
                                '& .MuiAutocomplete-paper': {
                                    bgcolor: darkMode ? '#2b2b2b' : '#fff',
                                    border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.12)',
                                    '& .MuiAutocomplete-option': {
                                        color: darkMode ? '#f5f7fa' : '#333',
                                        '&:hover': {
                                            bgcolor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
                                        }
                                    }
                                }
                            }
                        }
                    }}
                />
            </Box>

            {/* Оснащение - Кнопка добавления */}
            <Box>
                <Typography variant="subtitle2" sx={{
                    mb: 1,
                    fontWeight: 600,
                    color: darkMode ? fieldColors.equipment.dark : fieldColors.equipment.light,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                }}>
                    <BuildIcon sx={{ fontSize: '16px' }} />
                    Оснащение
                </Typography>
                <Button
                    fullWidth
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddCategoryClick}
                    sx={{
                        color: darkMode ? '#fff' : '#fff',
                        bgcolor: darkMode ? '#64b5f6' : '#1976d2',
                        '&:hover': {
                            bgcolor: darkMode ? '#90caf9' : '#1565c0',
                        },
                        fontSize: '0.875rem',
                        py: 1,
                        fontWeight: 600,
                        boxShadow: darkMode ? '0 2px 4px rgba(100, 181, 246, 0.3)' : '0 2px 4px rgba(25, 118, 210, 0.3)',
                    }}
                >
                    Добавить оснащение
                </Button>
            </Box>


            {/* Таблица данных */}
            <Box sx={{ mt: 2, mb: 1 }}>
                <TableContainer
                    component={Paper}
                    sx={{
                        bgcolor: darkMode ? '#2b2b2b' : '#fff',
                        boxShadow: darkMode ? '0 2px 8px rgba(0,0,0,0.4)' : '0 2px 8px rgba(0,0,0,0.1)',
                        border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.12)',
                        borderRadius: 2,
                        maxHeight: '60vh',
                        overflow: 'auto',
                        // Стилизация скроллбара
                        '&::-webkit-scrollbar': {
                            width: '8px',
                            height: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: darkMode ? '#1e1e1e' : '#f1f1f1',
                            borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            background: darkMode ? '#555' : '#c1c1c1',
                            borderRadius: '4px',
                            '&:hover': {
                                background: darkMode ? '#777' : '#a8a8a8',
                            },
                        },
                        '&::-webkit-scrollbar-corner': {
                            background: darkMode ? '#1e1e1e' : '#f1f1f1',
                        },
                    }}
                >
                    <Table
                        size="small"
                        stickyHeader
                        sx={{
                            '& .MuiTableCell-root': {
                                borderBottom: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                                borderRight: darkMode ? '1px solid rgba(255,255,255,0.15)' : '1px solid rgba(0,0,0,0.15)',
                            },
                            '& .MuiTableHead-root .MuiTableCell-root': {
                                borderBottom: darkMode ? '2px solid rgba(255,255,255,0.3)' : '2px solid rgba(0,0,0,0.3)',
                            },
                            '& .MuiTableRow-root:hover': {
                                bgcolor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                            },
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell align="center" sx={{
                                    bgcolor: darkMode ? '#1e1e1e' : '#f5f5f5',
                                    color: darkMode ? fieldColors.equipment.dark : fieldColors.equipment.light,
                                    fontWeight: 700,
                                    minWidth: 150,
                                    borderRight: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                                }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <BuildIcon sx={{ fontSize: '16px', mb: 0.5 }} />
                                        <Typography variant="subtitle2">Оснащение</Typography>
                                    </Box>
                                </TableCell>
                                <TableCell align="center" colSpan={2} sx={{
                                    bgcolor: darkMode ? '#1e1e1e' : '#f5f5f5',
                                    color: darkMode ? fieldColors.dates.dark : fieldColors.dates.light,
                                    fontWeight: 700,
                                    minWidth: 200,
                                    borderRight: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                                }}>

                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <CalendarIcon sx={{ fontSize: '16px' }} />
                                            <Tooltip title={'Плановое начало | Фактическое начало'}>
                                                <Typography variant="subtitle2">Начало строительства</Typography>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                </TableCell>
                                <TableCell align="center" colSpan={2} sx={{
                                    bgcolor: darkMode ? '#1e1e1e' : '#f5f5f5',
                                    color: darkMode ? fieldColors.dates.dark : fieldColors.dates.light,
                                    fontWeight: 700,
                                    minWidth: 200,
                                    borderRight: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                                }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <CalendarIcon sx={{ fontSize: '14px' }} />
                                            <Typography variant="subtitle2">Завершение строительства</Typography>
                                        </Box>
                                    </Box>
                                </TableCell>
                                <TableCell align="center" sx={{
                                    bgcolor: darkMode ? '#1e1e1e' : '#f5f5f5',
                                    color: darkMode ? fieldColors.stage.dark : fieldColors.stage.light,
                                    fontWeight: 700,
                                    minWidth: 80,
                                    borderRight: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                                }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <BuildIcon sx={{ fontSize: '14px' }} />
                                            <Typography variant="subtitle2">Этап</Typography>
                                        </Box>
                                    </Box>
                                </TableCell>
                                <TableCell align="center" sx={{
                                    bgcolor: darkMode ? '#1e1e1e' : '#f5f5f5',
                                    color: darkMode ? fieldColors.closing.dark : fieldColors.closing.light,
                                    fontWeight: 700,
                                    minWidth: 100,
                                    borderRight: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                                }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <CheckCircleIcon sx={{ fontSize: '14px', mb: 0.5 }} />
                                        <Typography variant="subtitle2">Закрытие без НДС</Typography>
                                    </Box>
                                </TableCell>
                                <TableCell align="center" sx={{
                                    bgcolor: darkMode ? '#1e1e1e' : '#f5f5f5',
                                    color: darkMode ? fieldColors.cost.dark : fieldColors.cost.light,
                                    fontWeight: 700,
                                    minWidth: 100,
                                    borderRight: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                                }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <AttachMoneyIcon sx={{ fontSize: '14px', mb: 0.5 }} />
                                        <Typography variant="subtitle2">Стоимость без НДС</Typography>
                                    </Box>
                                </TableCell>
                                <TableCell align="center" sx={{
                                    bgcolor: darkMode ? '#1e1e1e' : '#f5f5f5',
                                    color: darkMode ? fieldColors.comment.dark : fieldColors.comment.light,
                                    fontWeight: 700,
                                    minWidth: 150,
                                    borderRight: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                                }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <CommentIcon sx={{ fontSize: '14px', mb: 0.5 }} />
                                        <Typography variant="subtitle2">Комментарий</Typography>
                                    </Box>
                                </TableCell>
                                <TableCell align="center" sx={{
                                    bgcolor: darkMode ? '#1e1e1e' : '#f5f5f5',
                                    color: darkMode ? fieldColors.comment.dark : fieldColors.comment.light,
                                    fontWeight: 700,
                                    minWidth: 150,
                                    borderRight: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                                }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <CommentIcon sx={{ fontSize: '14px', mb: 0.5 }} />
                                        <Typography variant="subtitle2" sx={{ fontSize: '0.75rem' }}>Наименование по актам</Typography>
                                    </Box>
                                </TableCell>
                                <TableCell align="center" sx={{
                                    bgcolor: darkMode ? '#1e1e1e' : '#f5f5f5',
                                    color: darkMode ? fieldColors.responsible.dark : fieldColors.responsible.light,
                                    fontWeight: 700,
                                    minWidth: 150,
                                    borderRight: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                                }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <PersonIcon sx={{ fontSize: '14px' }} />
                                            <Typography variant="subtitle2">Ответственный</Typography>
                                        </Box>
                                    </Box>
                                </TableCell>
                                <TableCell align="center" sx={{
                                    bgcolor: darkMode ? '#1e1e1e' : '#f5f5f5',
                                    color: darkMode ? fieldColors.coexecutors.dark : fieldColors.coexecutors.light,
                                    fontWeight: 700,
                                    minWidth: 150,
                                }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <PersonIcon sx={{ fontSize: '14px' }} />
                                            <Typography variant="subtitle2">Соисполнители</Typography>
                                        </Box>
                                    </Box>
                                </TableCell>
                            </TableRow>
                            {/* <TableRow>
                                <TableCell sx={{ p: 0, border: 'none', bgcolor: darkMode ? '#1e1e1e' : '#f5f5f5' }} />
                                <TableCell align="center" sx={{
                                    bgcolor: darkMode ? '#1e1e1e' : '#f5f5f5',
                                    color: darkMode ? '#bbb' : '#666',
                                    fontSize: '0.7rem',
                                    p: 0.5,
                                    borderRight: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
                                }}>План</TableCell>
                                <TableCell align="center" sx={{
                                    bgcolor: darkMode ? '#1e1e1e' : '#f5f5f5',
                                    color: darkMode ? '#bbb' : '#666',
                                    fontSize: '0.7rem',
                                    p: 0.5,
                                    borderRight: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                                }}>Факт</TableCell>
                                <TableCell align="center" sx={{
                                    bgcolor: darkMode ? '#1e1e1e' : '#f5f5f5',
                                    color: darkMode ? '#bbb' : '#666',
                                    fontSize: '0.7rem',
                                    p: 0.5,
                                    borderRight: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
                                }}>План</TableCell>
                                <TableCell align="center" sx={{
                                    bgcolor: darkMode ? '#1e1e1e' : '#f5f5f5',
                                    color: darkMode ? '#bbb' : '#666',
                                    fontSize: '0.7rem',
                                    p: 0.5,
                                    borderRight: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                                }}>Факт</TableCell>
                                <TableCell sx={{ p: 0, border: 'none', bgcolor: darkMode ? '#1e1e1e' : '#f5f5f5', borderRight: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.12)' }} />
                                <TableCell sx={{ p: 0, border: 'none', bgcolor: darkMode ? '#1e1e1e' : '#f5f5f5', borderRight: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.12)' }} />
                                <TableCell sx={{ p: 0, border: 'none', bgcolor: darkMode ? '#1e1e1e' : '#f5f5f5', borderRight: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.12)' }} />
                                <TableCell sx={{ p: 0, border: 'none', bgcolor: darkMode ? '#1e1e1e' : '#f5f5f5', borderRight: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.12)' }} />
                                <TableCell sx={{ p: 0, border: 'none', bgcolor: darkMode ? '#1e1e1e' : '#f5f5f5', borderRight: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.12)' }} />
                                <TableCell sx={{ p: 0, border: 'none', bgcolor: darkMode ? '#1e1e1e' : '#f5f5f5' }} />
                            </TableRow> */}
                        </TableHead>
                        <TableBody>
                            {categoriesData.map((category, categoryIndex) => {
                                const isExpanded = expandedCategories.has(category.uuid);
                                const works = category.works || [];
                                const categoryName = category.equipment?.name || `Категория ${category.id}`;
                                const availableWorks = requirementsTree[String(category.id)] || [];
                                const existingWorkIds = works.map(w => w.id);
                                const worksToAdd = availableWorks.filter(w => !existingWorkIds.includes(w));

                                return (
                                    <React.Fragment key={category.uuid}>
                                        {/* Строка категории */}
                                        <TableRow sx={{
                                            '&:hover': {
                                                bgcolor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'
                                            }
                                        }}>
                                            {/* Оснащение с кнопкой раскрытия */}
                                            <TableCell sx={{
                                                color: darkMode ? '#fff' : '#000',
                                                borderRight: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                                                p: 0.5,
                                            }}>

                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    {existingWorkIds.length > 0 && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => toggleCategory(category.uuid)}
                                                            sx={{
                                                                p: 0.25,
                                                                color: darkMode ? '#64b5f6' : '#1976d2',
                                                            }}
                                                        >
                                                            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                        </IconButton>
                                                    )}
                                                    <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                                                        {categoryName}{works.length > 0 ? ` (${works.length})` : ''}
                                                    </Typography>
                                                    {availableWorks.length > 0 && worksToAdd.length > 0 && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => handleAddWorkClick(e, category.uuid)}
                                                            sx={{
                                                                p: 0.25,
                                                                color: darkMode ? '#81c784' : '#4caf50',
                                                            }}
                                                            title="Добавить работу"
                                                        >
                                                            <AddIcon sx={{ fontSize: '14px' }} />
                                                        </IconButton>
                                                    )}
                                                </Box>
                                            </TableCell>

                                            {/* Начало строительства - План */}
                                            <TableCell sx={{
                                                borderRight: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
                                                p: 0.5,
                                            }} align='center'>
                                                {works.length > 0 ? (
                                                    <Typography sx={{
                                                        fontSize: '0.75rem',
                                                        color: darkMode ? '#64b5f6' : '#1976d2',
                                                        fontWeight: 600,
                                                    }}>
                                                        {(() => {
                                                            const filteredValues = works.map(w => w.plan_start ? new Date(w.plan_start) : null).filter(Boolean);
                                                            const value = filteredValues.length > 0 ? Math.min(...filteredValues) : null;
                                                            return value ? formatDate(new Date(value).toUTCString(), true) : '';
                                                        })()}
                                                    </Typography>
                                                ) : (
                                                    <DatePicker
                                                        selected={category.plan_start ? new Date(category.plan_start) : null}
                                                        onChange={(date) => {
                                                            const formattedDate = date ?
                                                                `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` :
                                                                '';
                                                            setFormData(prev => {
                                                                const newCategories = [...(prev.categories || [])];
                                                                if (newCategories[categoryIndex]) {
                                                                    newCategories[categoryIndex] = {
                                                                        ...newCategories[categoryIndex],
                                                                        plan_start: formattedDate
                                                                    };
                                                                }
                                                                return { ...prev, categories: newCategories };
                                                            });
                                                        }}
                                                        locale="ru"
                                                        dateFormat="dd.MM.yyyy"
                                                        placeholderText="ДД.ММ.ГГГГ"
                                                        isClearable
                                                        showYearDropdown
                                                        showMonthDropdown
                                                        dropdownMode="select"
                                                        yearDropdownItemNumber={10}
                                                        scrollableYearDropdown
                                                        portalId="root-portal"
                                                        customInput={
                                                            <TextField
                                                                fullWidth
                                                                size="small"
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        fontSize: '0.75rem',
                                                                        bgcolor: darkMode ? '#3a3a3a' : '#fff',
                                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                                            borderColor: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                                                                        },
                                                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                            borderColor: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                                                        },
                                                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                            borderColor: darkMode ? fieldColors.dates.dark : fieldColors.dates.light,
                                                                            borderWidth: '2px',
                                                                        },
                                                                    },
                                                                    '& .MuiOutlinedInput-input': {
                                                                        p: 0.5,
                                                                        cursor: 'pointer',
                                                                        color: darkMode ? '#fff' : '#000',
                                                                    },
                                                                }}
                                                            />
                                                        }
                                                        calendarClassName={darkMode ? 'dark-datepicker' : 'light-datepicker'}
                                                    />
                                                )}
                                            </TableCell>

                                            {/* Начало строительства - Факт */}
                                            <TableCell sx={{
                                                borderRight: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
                                                p: 0.5,
                                            }} align='center'>
                                                {works.length > 0 ? (
                                                    <Typography sx={{
                                                        fontSize: '0.75rem',
                                                        color: darkMode ? '#4caf50' : '#2e7d32',
                                                        fontWeight: 600,
                                                    }}>
                                                        {(() => {
                                                            const filteredValues = works.map(w => w.fact_start ? new Date(w.fact_start) : null).filter(Boolean);
                                                            const value = filteredValues.length > 0 ? Math.min(...filteredValues) : null;
                                                            return value ? formatDate(new Date(value).toUTCString(), true) : '';
                                                        })()}
                                                    </Typography>
                                                ) : (
                                                    <DatePicker
                                                        selected={category.fact_start ? new Date(category.fact_start) : null}
                                                        onChange={(date) => {
                                                            const formattedDate = date ?
                                                                `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` :
                                                                '';
                                                            setFormData(prev => {
                                                                const newCategories = [...(prev.categories || [])];
                                                                if (newCategories[categoryIndex]) {
                                                                    newCategories[categoryIndex] = {
                                                                        ...newCategories[categoryIndex],
                                                                        fact_start: formattedDate
                                                                    };
                                                                }
                                                                return { ...prev, categories: newCategories };
                                                            });
                                                        }}
                                                        locale="ru"
                                                        dateFormat="dd.MM.yyyy"
                                                        placeholderText="ДД.ММ.ГГГГ"
                                                        isClearable
                                                        showYearDropdown
                                                        showMonthDropdown
                                                        dropdownMode="select"
                                                        yearDropdownItemNumber={10}
                                                        scrollableYearDropdown
                                                        portalId="root-portal"
                                                        customInput={
                                                            <TextField
                                                                fullWidth
                                                                size="small"
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        fontSize: '0.75rem',
                                                                        bgcolor: darkMode ? '#3a3a3a' : '#fff',
                                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                                            borderColor: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                                                                        },
                                                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                            borderColor: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                                                        },
                                                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                            borderColor: darkMode ? fieldColors.dates.dark : fieldColors.dates.light,
                                                                            borderWidth: '2px',
                                                                        },
                                                                    },
                                                                    '& .MuiOutlinedInput-input': {
                                                                        p: 0.5,
                                                                        cursor: 'pointer',
                                                                        color: darkMode ? '#fff' : '#000',
                                                                    },
                                                                }}
                                                            />
                                                        }
                                                        calendarClassName={darkMode ? 'dark-datepicker' : 'light-datepicker'}
                                                    />
                                                )}
                                            </TableCell>

                                            {/* Завершение строительства - План */}
                                            <TableCell sx={{
                                                borderRight: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
                                                p: 0.5,
                                            }} align='center'>
                                                {works.length > 0 ? (
                                                    <Typography sx={{
                                                        fontSize: '0.75rem',
                                                        color: darkMode ? '#64b5f6' : '#1976d2',
                                                        fontWeight: 600,
                                                    }}>
                                                        {(() => {
                                                            const filteredValues = works.map(w => w.plan_end ? new Date(w.plan_end) : null).filter(Boolean);
                                                            const value = filteredValues.length > 0 ? Math.max(...filteredValues) : null;
                                                            return value ? formatDate(new Date(value).toUTCString(), true) : '';
                                                        })()}
                                                    </Typography>
                                                ) : (
                                                    <DatePicker
                                                        selected={category.plan_end ? new Date(category.plan_end) : null}
                                                        onChange={(date) => {
                                                            const formattedDate = date ?
                                                                `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` :
                                                                '';
                                                            setFormData(prev => {
                                                                const newCategories = [...(prev.categories || [])];
                                                                if (newCategories[categoryIndex]) {
                                                                    newCategories[categoryIndex] = {
                                                                        ...newCategories[categoryIndex],
                                                                        plan_end: formattedDate
                                                                    };
                                                                }
                                                                return { ...prev, categories: newCategories };
                                                            });
                                                        }}
                                                        locale="ru"
                                                        dateFormat="dd.MM.yyyy"
                                                        placeholderText="ДД.ММ.ГГГГ"
                                                        isClearable
                                                        showYearDropdown
                                                        showMonthDropdown
                                                        dropdownMode="select"
                                                        yearDropdownItemNumber={10}
                                                        scrollableYearDropdown
                                                        portalId="root-portal"
                                                        customInput={
                                                            <TextField
                                                                fullWidth
                                                                size="small"
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        fontSize: '0.75rem',
                                                                        bgcolor: darkMode ? '#3a3a3a' : '#fff',
                                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                                            borderColor: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                                                                        },
                                                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                            borderColor: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                                                        },
                                                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                            borderColor: darkMode ? fieldColors.dates.dark : fieldColors.dates.light,
                                                                            borderWidth: '2px',
                                                                        },
                                                                    },
                                                                    '& .MuiOutlinedInput-input': {
                                                                        p: 0.5,
                                                                        cursor: 'pointer',
                                                                        color: darkMode ? '#fff' : '#000',
                                                                    },
                                                                }}
                                                            />
                                                        }
                                                        calendarClassName={darkMode ? 'dark-datepicker' : 'light-datepicker'}
                                                    />
                                                )}
                                            </TableCell>

                                            {/* Завершение строительства - Факт */}
                                            <TableCell sx={{
                                                borderRight: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
                                                p: 0.5,
                                            }} align='center'>
                                                {works.length > 0 ? (
                                                    <Typography sx={{
                                                        fontSize: '0.75rem',
                                                        color: darkMode ? '#4caf50' : '#2e7d32',
                                                        fontWeight: 600,
                                                    }}>
                                                        {(() => {
                                                            const filteredValues = works.map(w => w.fact_end ? new Date(w.fact_end) : null).filter(Boolean);
                                                            const value = filteredValues.length > 0 ? Math.max(...filteredValues) : null;
                                                            return value ? formatDate(new Date(value).toUTCString(), true) : '';
                                                        })()}
                                                    </Typography>
                                                ) : (
                                                    <DatePicker
                                                        selected={category.fact_end ? new Date(category.fact_end) : null}
                                                        onChange={(date) => {
                                                            const formattedDate = date ?
                                                                `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` :
                                                                '';
                                                            setFormData(prev => {
                                                                const newCategories = [...(prev.categories || [])];
                                                                if (newCategories[categoryIndex]) {
                                                                    newCategories[categoryIndex] = {
                                                                        ...newCategories[categoryIndex],
                                                                        fact_end: formattedDate
                                                                    };
                                                                }
                                                                return { ...prev, categories: newCategories };
                                                            });
                                                        }}
                                                        locale="ru"
                                                        dateFormat="dd.MM.yyyy"
                                                        placeholderText="ДД.ММ.ГГГГ"
                                                        isClearable
                                                        showYearDropdown
                                                        showMonthDropdown
                                                        dropdownMode="select"
                                                        yearDropdownItemNumber={10}
                                                        scrollableYearDropdown
                                                        portalId="root-portal"
                                                        customInput={
                                                            <TextField
                                                                fullWidth
                                                                size="small"
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        fontSize: '0.75rem',
                                                                        bgcolor: darkMode ? '#3a3a3a' : '#fff',
                                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                                            borderColor: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                                                                        },
                                                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                            borderColor: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                                                        },
                                                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                            borderColor: darkMode ? fieldColors.dates.dark : fieldColors.dates.light,
                                                                            borderWidth: '2px',
                                                                        },
                                                                    },
                                                                    '& .MuiOutlinedInput-input': {
                                                                        p: 0.5,
                                                                        cursor: 'pointer',
                                                                        color: darkMode ? '#fff' : '#000',
                                                                    },
                                                                }}
                                                            />
                                                        }
                                                        calendarClassName={darkMode ? 'dark-datepicker' : 'light-datepicker'}
                                                    />
                                                )}
                                            </TableCell>

                                            {/* Этап */}
                                            <TableCell sx={{
                                                borderRight: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                                                p: 0.5,
                                            }} align='center'>
                                                {works.length > 0 ? (
                                                    <Typography sx={{
                                                        fontSize: '0.75rem',
                                                        color: darkMode ? fieldColors.stage.dark : fieldColors.stage.light,
                                                        fontWeight: 600,
                                                    }}>
                                                        {(() => {
                                                            const filteredValues = works.map(w => parseFloat(w.stage)).filter(Boolean);
                                                            const value = filteredValues.length > 0 ? Math.max(...filteredValues) : null;
                                                            return value !== null && value !== undefined ? (value % 1 === 0 ? String(value) : parseFloat(value).toFixed(1)) : '';
                                                        })()}
                                                    </Typography>
                                                ) : (
                                                    <TextField
                                                        fullWidth
                                                        type="number"
                                                        size="small"
                                                        value={category.stage || ''}
                                                        onChange={(e) => {
                                                            const value = e.target.value.replace(',', '.');
                                                            if (value === '' || /^\d*\.?\d{0,1}$/.test(value)) {
                                                                setFormData(prev => {
                                                                    const newCategories = [...(prev.categories || [])];
                                                                    if (newCategories[categoryIndex]) {
                                                                        newCategories[categoryIndex] = {
                                                                            ...newCategories[categoryIndex],
                                                                            stage: value ? parseFloat(value) : null
                                                                        };
                                                                    }
                                                                    return { ...prev, categories: newCategories };
                                                                });
                                                            }
                                                        }}
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                fontSize: '0.75rem',
                                                                bgcolor: darkMode ? '#3a3a3a' : '#fff',
                                                                '& .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                                                                },
                                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                                                },
                                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: darkMode ? fieldColors.closing.dark : fieldColors.closing.light,
                                                                    borderWidth: '2px',
                                                                },
                                                            },
                                                            '& .MuiOutlinedInput-input': {
                                                                p: 0.5,
                                                                color: darkMode ? '#fff' : '#000',
                                                            },
                                                        }}
                                                    />
                                                )}
                                            </TableCell>

                                            {/* Закрытие - показываем сумму работ, если они есть, иначе редактируемое поле */}
                                            <TableCell sx={{
                                                borderRight: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                                                p: 0.5,
                                            }} align='center'>
                                                {works.length > 0 ? (
                                                    <Typography sx={{
                                                        fontSize: '0.75rem',
                                                        color: darkMode ? '#ff8f00' : '#e65100',
                                                        fontWeight: 600,
                                                    }}>
                                                        {new Intl.NumberFormat('ru-RU', {
                                                            style: 'currency',
                                                            currency: 'RUB',
                                                        }).format(works.reduce((sum, work) => sum + (Number(work.closing || 0)), 0))}
                                                    </Typography>
                                                ) : (
                                                    <TextField
                                                        fullWidth
                                                        type="number"
                                                        size="small"
                                                        value={category.closing || ''}
                                                        onChange={(e) => {
                                                            const value = e.target.value.replace(',', '.');
                                                            if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                                                                setFormData(prev => {
                                                                    const newCategories = [...(prev.categories || [])];
                                                                    if (newCategories[categoryIndex]) {
                                                                        newCategories[categoryIndex] = {
                                                                            ...newCategories[categoryIndex],
                                                                            closing: value ? parseFloat(value) : null
                                                                        };
                                                                    }
                                                                    return { ...prev, categories: newCategories };
                                                                });
                                                            }
                                                        }}
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                fontSize: '0.75rem',
                                                                bgcolor: darkMode ? '#3a3a3a' : '#fff',
                                                                '& .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                                                                },
                                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                                                },
                                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: darkMode ? fieldColors.comment.dark : fieldColors.comment.light,
                                                                    borderWidth: '2px',
                                                                },
                                                            },
                                                            '& .MuiOutlinedInput-input': {
                                                                p: 0.5,
                                                                color: darkMode ? '#fff' : '#000',
                                                            },
                                                        }}
                                                    />
                                                )}
                                            </TableCell>

                                            {/* Стоимость - показываем сумму работ, если они есть, иначе редактируемое поле */}
                                            <TableCell sx={{
                                                borderRight: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                                                p: 0.5,
                                            }} align='center'>
                                                {works.length > 0 ? (
                                                    <Typography sx={{
                                                        fontSize: '0.75rem',
                                                        color: darkMode ? '#e91e63' : '#c2185b',
                                                        fontWeight: 600,
                                                    }}>
                                                        {new Intl.NumberFormat('ru-RU', {
                                                            style: 'currency',
                                                            currency: 'RUB',
                                                        }).format(works.reduce((sum, work) => sum + (Number(work.cost || 0)), 0))}
                                                    </Typography>
                                                ) : (
                                                    <TextField
                                                        fullWidth
                                                        type="number"
                                                        size="small"
                                                        value={category.cost || ''}
                                                        onChange={(e) => {
                                                            const value = e.target.value.replace(',', '.');
                                                            if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                                                                setFormData(prev => {
                                                                    const newCategories = [...(prev.categories || [])];
                                                                    if (newCategories[categoryIndex]) {
                                                                        newCategories[categoryIndex] = {
                                                                            ...newCategories[categoryIndex],
                                                                            cost: value ? parseFloat(value) : null
                                                                        };
                                                                    }
                                                                    return { ...prev, categories: newCategories };
                                                                });
                                                            }
                                                        }}
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                fontSize: '0.75rem',
                                                                bgcolor: darkMode ? '#3a3a3a' : '#fff',
                                                                '& .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                                                                },
                                                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                                                },
                                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: darkMode ? fieldColors.comment.dark : fieldColors.comment.light,
                                                                    borderWidth: '2px',
                                                                },
                                                            },
                                                            '& .MuiOutlinedInput-input': {
                                                                p: 0.5,
                                                                color: darkMode ? '#fff' : '#000',
                                                            },
                                                        }}
                                                    />
                                                )}
                                            </TableCell>

                                            {/* Комментарий */}
                                            <TableCell sx={{
                                                borderRight: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                                                p: 0.5,
                                            }}>
                                                <TextField
                                                    fullWidth
                                                    multiline
                                                    rows={2}
                                                    size="small"
                                                    value={category.comment || ''}
                                                    onChange={(e) => {
                                                        setFormData(prev => {
                                                            const newCategories = [...(prev.categories || [])];
                                                            if (newCategories[categoryIndex]) {
                                                                newCategories[categoryIndex] = {
                                                                    ...newCategories[categoryIndex],
                                                                    comment: e.target.value
                                                                };
                                                            }
                                                            return { ...prev, categories: newCategories };
                                                        });
                                                    }}
                                                    placeholder="Markdown..."
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            fontSize: '0.75rem',
                                                            bgcolor: darkMode ? '#3a3a3a' : '#fff',
                                                            '& .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                                                            },
                                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                                            },
                                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: darkMode ? fieldColors.comment.dark : fieldColors.comment.light,
                                                                borderWidth: '2px',
                                                            },
                                                        },
                                                        '& .MuiOutlinedInput-input': {
                                                            p: 0.5,
                                                            color: darkMode ? '#fff' : '#000',
                                                        },
                                                    }}
                                                />
                                            </TableCell>

                                            {/* Наименование по актам */}
                                            <TableCell sx={{
                                                borderRight: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                                                p: 0.5,
                                            }}>
                                                <TextField
                                                    fullWidth
                                                    multiline
                                                    rows={2}
                                                    size="small"
                                                    value={category.name_by_acts || ''}
                                                    onChange={(e) => {
                                                        setFormData(prev => {
                                                            const newCategories = [...(prev.categories || [])];
                                                            if (newCategories[categoryIndex]) {
                                                                newCategories[categoryIndex] = {
                                                                    ...newCategories[categoryIndex],
                                                                    name_by_acts: e.target.value
                                                                };
                                                            }
                                                            return { ...prev, categories: newCategories };
                                                        });
                                                    }}
                                                    placeholder="Наименование по актам (Markdown...)"
                                                    sx={{
                                                        '& .MuiOutlinedInput-root': {
                                                            fontSize: '0.75rem',
                                                            bgcolor: darkMode ? '#3a3a3a' : '#fff',
                                                            '& .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                                                            },
                                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                                            },
                                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                borderColor: darkMode ? fieldColors.comment.dark : fieldColors.comment.light,
                                                                borderWidth: '2px',
                                                            },
                                                        },
                                                        '& .MuiOutlinedInput-input': {
                                                            p: 0.5,
                                                            color: darkMode ? '#fff' : '#000',
                                                        },
                                                    }}
                                                />
                                            </TableCell>

                                            {/* Ответственный */}
                                            <TableCell sx={{
                                                borderRight: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                                                p: 0.5,
                                            }} align='center'>
                                                {works.length > 0 ? (
                                                    <Typography sx={{
                                                        fontSize: '0.75rem',
                                                        bgcolor: darkMode ? 'rgba(211, 47, 47, 0.2)' : 'rgba(211, 47, 47, 0.1)',
                                                        color: darkMode ? '#f44336' : '#d32f2f',
                                                        borderRadius: '8px',
                                                        fontWeight: 600,
                                                        p: 0.5,
                                                    }}>
                                                        {(() => {
                                                            const value = [...new Set(works.map(w => w.responsible).filter(Boolean))];
                                                            return value.length > 0 ? value.map(v => {
                                                                const resp = responsible.find(r => r.id === v);
                                                                return resp ? resp.name : '';
                                                            }).filter(Boolean).join(', ') : '';
                                                        })()}
                                                    </Typography>
                                                ) : (
                                                    <Autocomplete
                                                        fullWidth
                                                        size="small"
                                                        options={responsible}
                                                        getOptionLabel={(option) => option.name}
                                                        value={category.responsible || null}
                                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                                        onChange={(event, value) => {
                                                            setFormData(prev => {
                                                                const newCategories = [...(prev.categories || [])];
                                                                if (newCategories[categoryIndex]) {
                                                                    const updatedCoexecutors = (newCategories[categoryIndex].coexecutors || []).filter(c => c.id !== value?.id);
                                                                    newCategories[categoryIndex] = {
                                                                        ...newCategories[categoryIndex],
                                                                        responsible: value,
                                                                        coexecutors: updatedCoexecutors
                                                                    };
                                                                }
                                                                return { ...prev, categories: newCategories };
                                                            });
                                                        }}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                size="small"
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        fontSize: '0.75rem',
                                                                        bgcolor: darkMode ? '#3a3a3a' : '#fff',
                                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                                            borderColor: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                                                                        },
                                                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                            borderColor: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                                                        },
                                                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                            borderColor: darkMode ? fieldColors.responsible.dark : fieldColors.responsible.light,
                                                                            borderWidth: '2px',
                                                                        },
                                                                    },
                                                                    '& .MuiOutlinedInput-input': {
                                                                        p: 0.5,
                                                                        color: darkMode ? '#fff' : '#000',
                                                                    },
                                                                }}
                                                            />
                                                        )}
                                                        slotProps={{
                                                            popper: {
                                                                sx: {
                                                                    zIndex: 13000,
                                                                    '& .MuiAutocomplete-paper': {

                                                                        color: darkMode ? '#f5f7fa' : '#333',
                                                                        bgcolor: darkMode ? '#3a3a3a' : '#fff',
                                                                    }
                                                                }
                                                            }
                                                        }}
                                                    />
                                                )}
                                            </TableCell>

                                            {/* Соисполнители */}
                                            <TableCell sx={{ p: 0.5 }} align='center'>
                                                {works.length > 0 ? (
                                                    <Typography sx={{
                                                        fontSize: '0.75rem',
                                                        bgcolor: darkMode ? 'rgba(245, 124, 0, 0.2)' : 'rgba(245, 124, 0, 0.1)',
                                                        color: darkMode ? '#ffb74d' : '#f57c00',
                                                        borderRadius: '8px',
                                                        fontWeight: 600,
                                                        p: 0.5,
                                                    }}>
                                                        {(() => {
                                                            const value = [...new Set(works.map(w => Array.isArray(w.coexecutors) ? w.coexecutors : [w.coexecutors]).flat().filter(Boolean))];
                                                            return value.length > 0 ? value.map(v => {
                                                                const coexec = responsible.find(r => r.id === v);
                                                                return coexec ? coexec.name : '';
                                                            }).filter(Boolean).join(', ') : '';
                                                        })()}
                                                    </Typography>
                                                ) : (
                                                    <Autocomplete
                                                        fullWidth
                                                        multiple
                                                        size="small"
                                                        disableCloseOnSelect
                                                        disabled={!category.responsible}
                                                        options={responsible.filter(option => option.id !== category.responsible?.id)}
                                                        getOptionLabel={(option) => option.name}
                                                        value={category.coexecutors || []}
                                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                                        onChange={(event, value) => {
                                                            setFormData(prev => {
                                                                const newCategories = [...(prev.categories || [])];
                                                                if (newCategories[categoryIndex]) {
                                                                    newCategories[categoryIndex] = {
                                                                        ...newCategories[categoryIndex],
                                                                        coexecutors: value
                                                                    };
                                                                }
                                                                return { ...prev, categories: newCategories };
                                                            });
                                                        }}
                                                        renderTags={(value, getTagProps) =>
                                                            value.map((option, index) => (
                                                                <Chip
                                                                    {...getTagProps({ index })}
                                                                    key={option.id}
                                                                    label={option.name}
                                                                    size="small"
                                                                    sx={{
                                                                        bgcolor: darkMode ? 'rgba(245, 124, 0, 0.2)' : 'rgba(245, 124, 0, 0.1)',
                                                                        color: darkMode ? '#ffb74d' : '#f57c00',
                                                                        fontSize: '0.65rem',
                                                                        height: 'auto',
                                                                        minHeight: '20px',
                                                                        '& .MuiChip-label': {
                                                                            whiteSpace: 'normal',
                                                                            wordBreak: 'break-word',
                                                                            textAlign: 'center',
                                                                            lineHeight: '1.2',
                                                                            padding: '2px 6px',
                                                                        }
                                                                    }}
                                                                />
                                                            ))
                                                        }
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                size="small"
                                                                sx={{
                                                                    '& .MuiOutlinedInput-root': {
                                                                        fontSize: '0.75rem',
                                                                        bgcolor: darkMode ? '#3a3a3a' : '#fff',
                                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                                            borderColor: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                                                                        },
                                                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                            borderColor: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                                                        },
                                                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                            borderColor: darkMode ? fieldColors.coexecutors.dark : fieldColors.coexecutors.light,
                                                                            borderWidth: '2px',
                                                                        },
                                                                    },
                                                                    '& .MuiOutlinedInput-input': {
                                                                        p: 0.5,
                                                                        color: darkMode ? '#fff' : '#000',
                                                                    },
                                                                }}
                                                            />
                                                        )}
                                                        slotProps={{
                                                            popper: {
                                                                sx: {
                                                                    zIndex: 13000,
                                                                    '& .MuiAutocomplete-paper': {

                                                                        color: darkMode ? '#f5f7fa' : '#333',
                                                                        bgcolor: darkMode ? '#3a3a3a' : '#fff',
                                                                    }
                                                                }
                                                            }
                                                        }}
                                                    />
                                                )}
                                            </TableCell>
                                        </TableRow>

                                        {/* Строки работ (если категория раскрыта) */}
                                        {isExpanded && works.map((work, workIndex) => {
                                            const workName = equipment.find(eq => Number(eq.id) === Number(work.id))?.name || `Работа ${work.id}`;
                                            return (
                                                <TableRow key={`work-${category.uuid}-${work.id}-${workIndex}`} sx={{
                                                    bgcolor: darkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
                                                    '&:hover': {
                                                        bgcolor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'
                                                    }
                                                }}>
                                                    <TableCell sx={{
                                                        color: darkMode ? '#bbb' : '#666',
                                                        borderRight: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                                                        p: 0.5,
                                                        pl: 4,
                                                    }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                                                                {workName}
                                                            </Typography>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleDeleteWork(categoryIndex, workIndex)}
                                                                sx={{
                                                                    p: 0.25,
                                                                    color: darkMode ? '#ef5350' : '#d32f2f',
                                                                }}
                                                                title="Удалить работу"
                                                            >
                                                                <DeleteIcon sx={{ fontSize: '12px' }} />
                                                            </IconButton>
                                                        </Box>
                                                    </TableCell>
                                                    {/* Начало строительства - План */}
                                                    <TableCell sx={{
                                                        borderRight: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
                                                        p: 0.5,
                                                    }}>
                                                        <DatePicker
                                                            selected={work.plan_start ? new Date(work.plan_start) : null}
                                                            onChange={(date) => {
                                                                const formattedDate = date ?
                                                                    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` :
                                                                    '';
                                                                handleUpdateWork(categoryIndex, workIndex, 'plan_start', formattedDate);
                                                            }}
                                                            locale="ru"
                                                            dateFormat="dd.MM.yyyy"
                                                            placeholderText="ДД.ММ.ГГГГ"
                                                            isClearable
                                                            showYearDropdown
                                                            showMonthDropdown
                                                            dropdownMode="select"
                                                            yearDropdownItemNumber={10}
                                                            scrollableYearDropdown
                                                            portalId="root-portal"
                                                            customInput={
                                                                <TextField
                                                                    fullWidth
                                                                    size="small"
                                                                    sx={{
                                                                        '& .MuiOutlinedInput-root': {
                                                                            fontSize: '0.7rem',
                                                                            bgcolor: darkMode ? '#3a3a3a' : '#fff',
                                                                            '& .MuiOutlinedInput-notchedOutline': {
                                                                                borderColor: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                                                                            },
                                                                        },
                                                                        '& .MuiOutlinedInput-input': {
                                                                            p: 0.5,
                                                                            cursor: 'pointer',
                                                                            color: darkMode ? '#fff' : '#000',
                                                                        },
                                                                    }}
                                                                />
                                                            }
                                                            calendarClassName={darkMode ? 'dark-datepicker' : 'light-datepicker'}
                                                        />
                                                    </TableCell>
                                                    {/* Начало строительства - Факт */}
                                                    <TableCell sx={{
                                                        borderRight: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
                                                        p: 0.5,
                                                    }}>
                                                        <DatePicker
                                                            selected={work.fact_start ? new Date(work.fact_start) : null}
                                                            onChange={(date) => {
                                                                const formattedDate = date ?
                                                                    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` :
                                                                    '';
                                                                handleUpdateWork(categoryIndex, workIndex, 'fact_start', formattedDate);
                                                            }}
                                                            locale="ru"
                                                            dateFormat="dd.MM.yyyy"
                                                            placeholderText="ДД.ММ.ГГГГ"
                                                            isClearable
                                                            showYearDropdown
                                                            showMonthDropdown
                                                            dropdownMode="select"
                                                            yearDropdownItemNumber={10}
                                                            scrollableYearDropdown
                                                            portalId="root-portal"
                                                            customInput={
                                                                <TextField
                                                                    fullWidth
                                                                    size="small"
                                                                    sx={{
                                                                        '& .MuiOutlinedInput-root': {
                                                                            fontSize: '0.7rem',
                                                                            bgcolor: darkMode ? '#3a3a3a' : '#fff',
                                                                            '& .MuiOutlinedInput-notchedOutline': {
                                                                                borderColor: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                                                                            },
                                                                        },
                                                                        '& .MuiOutlinedInput-input': {
                                                                            p: 0.5,
                                                                            cursor: 'pointer',
                                                                            color: darkMode ? '#fff' : '#000',
                                                                        },
                                                                    }}
                                                                />
                                                            }
                                                            calendarClassName={darkMode ? 'dark-datepicker' : 'light-datepicker'}
                                                        />
                                                    </TableCell>
                                                    {/* Завершение строительства - План */}
                                                    <TableCell sx={{
                                                        borderRight: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
                                                        p: 0.5,
                                                    }}>
                                                        <DatePicker
                                                            selected={work.plan_end ? new Date(work.plan_end) : null}
                                                            onChange={(date) => {
                                                                const formattedDate = date ?
                                                                    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` :
                                                                    '';
                                                                handleUpdateWork(categoryIndex, workIndex, 'plan_end', formattedDate);
                                                            }}
                                                            locale="ru"
                                                            dateFormat="dd.MM.yyyy"
                                                            placeholderText="ДД.ММ.ГГГГ"
                                                            isClearable
                                                            showYearDropdown
                                                            showMonthDropdown
                                                            dropdownMode="select"
                                                            yearDropdownItemNumber={10}
                                                            scrollableYearDropdown
                                                            portalId="root-portal"
                                                            customInput={
                                                                <TextField
                                                                    fullWidth
                                                                    size="small"
                                                                    sx={{
                                                                        '& .MuiOutlinedInput-root': {
                                                                            fontSize: '0.7rem',
                                                                            bgcolor: darkMode ? '#3a3a3a' : '#fff',
                                                                            '& .MuiOutlinedInput-notchedOutline': {
                                                                                borderColor: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                                                                            },
                                                                        },
                                                                        '& .MuiOutlinedInput-input': {
                                                                            p: 0.5,
                                                                            cursor: 'pointer',
                                                                            color: darkMode ? '#fff' : '#000',
                                                                        },
                                                                    }}
                                                                />
                                                            }
                                                            calendarClassName={darkMode ? 'dark-datepicker' : 'light-datepicker'}
                                                        />
                                                    </TableCell>
                                                    {/* Завершение строительства - Факт */}
                                                    <TableCell sx={{
                                                        borderRight: darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
                                                        p: 0.5,
                                                    }}>
                                                        <DatePicker
                                                            selected={work.fact_end ? new Date(work.fact_end) : null}
                                                            onChange={(date) => {
                                                                const formattedDate = date ?
                                                                    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` :
                                                                    '';
                                                                handleUpdateWork(categoryIndex, workIndex, 'fact_end', formattedDate);
                                                            }}
                                                            locale="ru"
                                                            dateFormat="dd.MM.yyyy"
                                                            placeholderText="ДД.ММ.ГГГГ"
                                                            isClearable
                                                            showYearDropdown
                                                            showMonthDropdown
                                                            dropdownMode="select"
                                                            yearDropdownItemNumber={10}
                                                            scrollableYearDropdown
                                                            portalId="root-portal"
                                                            customInput={
                                                                <TextField
                                                                    fullWidth
                                                                    size="small"
                                                                    sx={{
                                                                        '& .MuiOutlinedInput-root': {
                                                                            fontSize: '0.7rem',
                                                                            bgcolor: darkMode ? '#3a3a3a' : '#fff',
                                                                            '& .MuiOutlinedInput-notchedOutline': {
                                                                                borderColor: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                                                                            },
                                                                        },
                                                                        '& .MuiOutlinedInput-input': {
                                                                            p: 0.5,
                                                                            cursor: 'pointer',
                                                                            color: darkMode ? '#fff' : '#000',
                                                                        },
                                                                    }}
                                                                />
                                                            }
                                                            calendarClassName={darkMode ? 'dark-datepicker' : 'light-datepicker'}
                                                        />
                                                    </TableCell>
                                                    {/* Этап */}
                                                    <TableCell sx={{
                                                        borderRight: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                                                        p: 0.5,
                                                    }}>
                                                        <TextField
                                                            fullWidth
                                                            type="number"
                                                            size="small"
                                                            value={work.stage || ''}
                                                            onChange={(e) => {
                                                                const value = e.target.value.replace(',', '.');
                                                                if (value === '' || /^\d*\.?\d{0,1}$/.test(value)) {
                                                                    handleUpdateWork(categoryIndex, workIndex, 'stage', value ? parseFloat(value) : null);
                                                                }
                                                            }}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    fontSize: '0.7rem',
                                                                    bgcolor: darkMode ? '#3a3a3a' : '#fff',
                                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                                        borderColor: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                                                                    },
                                                                },
                                                                '& .MuiOutlinedInput-input': {
                                                                    p: 0.5,
                                                                    color: darkMode ? '#fff' : '#000',
                                                                },
                                                            }}
                                                        />
                                                    </TableCell>
                                                    {/* Закрытие */}
                                                    <TableCell sx={{
                                                        borderRight: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                                                        p: 0.5,
                                                    }}>
                                                        <TextField
                                                            fullWidth
                                                            type="number"
                                                            size="small"
                                                            value={work.closing || ''}
                                                            onChange={(e) => {
                                                                const value = e.target.value.replace(',', '.');
                                                                if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                                                                    handleUpdateWork(categoryIndex, workIndex, 'closing', value ? parseFloat(value) : null);
                                                                }
                                                            }}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    fontSize: '0.7rem',
                                                                    bgcolor: darkMode ? '#3a3a3a' : '#fff',
                                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                                        borderColor: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                                                                    },
                                                                },
                                                                '& .MuiOutlinedInput-input': {
                                                                    p: 0.5,
                                                                    color: darkMode ? '#fff' : '#000',
                                                                },
                                                            }}
                                                        />
                                                    </TableCell>
                                                    {/* Стоимость */}
                                                    <TableCell sx={{
                                                        borderRight: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                                                        p: 0.5,
                                                    }}>
                                                        <TextField
                                                            fullWidth
                                                            type="number"
                                                            size="small"
                                                            value={work.cost || ''}
                                                            onChange={(e) => {
                                                                const value = e.target.value.replace(',', '.');
                                                                if (value === '' || /^\d*\.?\d{0,2}$/.test(value)) {
                                                                    handleUpdateWork(categoryIndex, workIndex, 'cost', value ? parseFloat(value) : null);
                                                                }
                                                            }}
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    fontSize: '0.7rem',
                                                                    bgcolor: darkMode ? '#3a3a3a' : '#fff',
                                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                                        borderColor: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                                                                    },
                                                                },
                                                                '& .MuiOutlinedInput-input': {
                                                                    p: 0.5,
                                                                    color: darkMode ? '#fff' : '#000',
                                                                },
                                                            }}
                                                        />
                                                    </TableCell>
                                                    {/* Комментарий */}
                                                    <TableCell sx={{
                                                        borderRight: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                                                        p: 0.5,
                                                    }}>
                                                        <TextField
                                                            fullWidth
                                                            multiline
                                                            rows={2}
                                                            size="small"
                                                            value={work.comment || ''}
                                                            onChange={(e) => {
                                                                handleUpdateWork(categoryIndex, workIndex, 'comment', e.target.value);
                                                            }}
                                                            placeholder="Комментарий (Markdown...)"
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    fontSize: '0.7rem',
                                                                    bgcolor: darkMode ? '#3a3a3a' : '#fff',
                                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                                        borderColor: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                                                                    },
                                                                },
                                                                '& .MuiOutlinedInput-input': {
                                                                    p: 0.5,
                                                                    color: darkMode ? '#fff' : '#000',
                                                                },
                                                            }}
                                                        />
                                                    </TableCell>
                                                    {/* Наименование по актам */}
                                                    <TableCell sx={{
                                                        borderRight: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                                                        p: 0.5,
                                                    }}>
                                                        <TextField
                                                            fullWidth
                                                            multiline
                                                            rows={2}
                                                            size="small"
                                                            value={work.name_by_acts || ''}
                                                            onChange={(e) => {
                                                                handleUpdateWork(categoryIndex, workIndex, 'name_by_acts', e.target.value);
                                                            }}
                                                            placeholder="Наименование по актам (Markdown...)"
                                                            sx={{
                                                                '& .MuiOutlinedInput-root': {
                                                                    fontSize: '0.7rem',
                                                                    bgcolor: darkMode ? '#3a3a3a' : '#fff',
                                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                                        borderColor: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                                                                    },
                                                                },
                                                                '& .MuiOutlinedInput-input': {
                                                                    p: 0.5,
                                                                    color: darkMode ? '#fff' : '#000',
                                                                },
                                                            }}
                                                        />
                                                    </TableCell>
                                                    {/* Ответственный */}
                                                    <TableCell sx={{
                                                        borderRight: darkMode ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(0,0,0,0.2)',
                                                        p: 0.5,
                                                    }}>
                                                        <Autocomplete
                                                            fullWidth
                                                            size="small"
                                                            options={responsible}
                                                            getOptionLabel={(option) => option.name}
                                                            value={work.responsible ? responsible.find(r => r.id === work.responsible) || null : null}
                                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                                            onChange={(event, value) => {
                                                                handleUpdateWork(categoryIndex, workIndex, 'responsible', value ? value.id : null);
                                                            }}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    size="small"
                                                                    sx={{
                                                                        '& .MuiOutlinedInput-root': {
                                                                            fontSize: '0.7rem',
                                                                            bgcolor: darkMode ? '#3a3a3a' : '#fff',
                                                                            '& .MuiOutlinedInput-notchedOutline': {
                                                                                borderColor: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                                                                            },
                                                                        },
                                                                        '& .MuiOutlinedInput-input': {
                                                                            p: 0.5,
                                                                            color: darkMode ? '#fff' : '#000',
                                                                        },
                                                                    }}
                                                                />
                                                            )}
                                                            slotProps={{
                                                                popper: {
                                                                    sx: {
                                                                        zIndex: 13000,
                                                                        '& .MuiAutocomplete-paper': {
                                                                            color: darkMode ? '#f5f7fa' : '#333',
                                                                            bgcolor: darkMode ? '#3a3a3a' : '#fff',
                                                                        }
                                                                    }
                                                                }
                                                            }}
                                                        />
                                                    </TableCell>
                                                    {/* Соисполнители */}
                                                    <TableCell sx={{ p: 0.5 }}>
                                                        <Autocomplete
                                                            fullWidth
                                                            multiple
                                                            size="small"
                                                            disableCloseOnSelect
                                                            // disabled={!work.responsible}
                                                            options={responsible.filter(option => option.id !== work.responsible)}
                                                            getOptionLabel={(option) => option.name}
                                                            value={(() => {
                                                                console.log(work.coexecutors, responsible)
                                                                if (!work.coexecutors) return [];
                                                                const coexecIds = Array.isArray(work.coexecutors) ? work.coexecutors : [work.coexecutors];
                                                                return responsible.filter(r => coexecIds.includes(r.id));
                                                            })()}
                                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                                            onChange={(event, value) => {
                                                                const coexecIds = value.map(v => v.id);
                                                                handleUpdateWork(categoryIndex, workIndex, 'coexecutors', coexecIds.length > 0 ? coexecIds : null);
                                                            }}
                                                            renderTags={(value, getTagProps) =>
                                                                value.map((option, index) => (
                                                                    <Chip
                                                                        {...getTagProps({ index })}
                                                                        key={option.id}
                                                                        label={option.name}
                                                                        size="small"
                                                                        sx={{
                                                                            bgcolor: darkMode ? 'rgba(245, 124, 0, 0.2)' : 'rgba(245, 124, 0, 0.1)',
                                                                            color: darkMode ? '#ffb74d' : '#f57c00',
                                                                            fontSize: '0.65rem',
                                                                            height: 'auto',
                                                                            minHeight: '20px',
                                                                            '& .MuiChip-label': {
                                                                                whiteSpace: 'normal',
                                                                                wordBreak: 'break-word',
                                                                                textAlign: 'center',
                                                                                lineHeight: '1.2',
                                                                                padding: '2px 6px',
                                                                            }
                                                                        }}
                                                                    />
                                                                ))
                                                            }
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    size="small"
                                                                    sx={{
                                                                        '& .MuiOutlinedInput-root': {
                                                                            fontSize: '0.7rem',
                                                                            bgcolor: darkMode ? '#3a3a3a' : '#fff',
                                                                            '& .MuiOutlinedInput-notchedOutline': {
                                                                                borderColor: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
                                                                            },
                                                                        },
                                                                        '& .MuiOutlinedInput-input': {
                                                                            p: 0.5,
                                                                            color: darkMode ? '#fff' : '#000',
                                                                        },
                                                                    }}
                                                                />
                                                            )}
                                                            slotProps={{
                                                                popper: {
                                                                    sx: {
                                                                        zIndex: 13000,
                                                                        '& .MuiAutocomplete-paper': {
                                                                            color: darkMode ? '#f5f7fa' : '#333',
                                                                            bgcolor: darkMode ? '#3a3a3a' : '#fff',
                                                                        }
                                                                    }
                                                                }
                                                            }}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </React.Fragment>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

        </Box >
    );
};

const ObjectDialog = ({
    open,
    currentUser,
    onClose,
    objectData = null,
    darkMode = false,
    responsible = [],
    equipment = [],
    stages = [],
    projects = [],
    selectedProjectId = null
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isEditing = !!objectData;
    const transformers = useSelector(selectTransformers)




    const [formData, setFormData] = useState({
        object: null,
        project: null,
        categories: [],
        creator: null,
        modifier: null,
    });

    // Состояние для хранения проектов, где уже есть данный объект
    const [projectsWithObject, setProjectsWithObject] = useState([]);
    const [objectsIntProject, setObjectsIntProject] = useState([]);
    const [isCheckingProjects, setIsCheckingProjects] = useState(false);

    // Состояние для раскрытых категорий
    const [expandedCategories, setExpandedCategories] = useState(new Set());

    // Состояние для меню добавления категорий
    const [categoryMenuAnchor, setCategoryMenuAnchor] = useState(null);

    // Состояние для меню добавления работ
    const [workMenuAnchor, setWorkMenuAnchor] = useState(null);
    const [selectedCategoryForWork, setSelectedCategoryForWork] = useState(null);

    const handleCategoryMenuClose = () => {
        setCategoryMenuAnchor(null);
    };

    const handleAddCategoryClick = (event) => {
        event.stopPropagation();
        setCategoryMenuAnchor(event.currentTarget);
    };

    const handleAddCategory = (categoryId) => {
        const selectedEquipment = equipment.find(eq => Number(eq.id) === Number(categoryId));
        if (!selectedEquipment) return;

        // Создаем новую категорию с уникальным uuid
        const newCategory = {
            id: selectedEquipment.id,
            uuid: crypto.randomUUID(),
            equipment: selectedEquipment,
            plan_start: null,
            fact_start: null,
            plan_end: null,
            fact_end: null,
            stage: null,
            responsible: null,
            coexecutors: [],
            closing: null,
            cost: null,
            comment: null,
            name_by_acts: null,
            works: []
        };

        setFormData(prev => ({
            ...prev,
            categories: [...(prev.categories || []), newCategory]
        }));

        handleCategoryMenuClose();
    };

    const handleWorkMenuClose = () => {
        setWorkMenuAnchor(null);
        setSelectedCategoryForWork(null);
    };

    const objects = useMemo(() => {
        return transformers.filter(ktp => !objectsIntProject.includes(ktp.fid))
    }, [transformers, objectsIntProject])

    // Функция для проверки существования объекта в проектах
    const checkObjectInProjects = useCallback(async (objectId) => {
        if (!objectId) return;

        setIsCheckingProjects(true);
        try {
            const response = await $authHost.get('/apig/reestr-v3', {
                params: {
                    object_id: objectId,
                    object_type: 'transformers',
                    is_active: 'true'
                }
            });

            if (response.data.success) {
                const existingProjects = response.data.data
                    .filter(obj => obj.project_id && obj.id !== (objectData?.id || null))
                    .map(obj => obj.project_id);
                setProjectsWithObject(existingProjects);
            }
        } catch (error) {
            console.error('Error checking object in projects:', error);
            setProjectsWithObject([]);
        } finally {
            setIsCheckingProjects(false);
        }
    }, [objectData?.id]);

    // Функция для проверки существования объекта в проектах
    const getObjecstInProject = useCallback(async (projectId) => {
        if (!projectId) return;

        setIsCheckingProjects(true);
        try {
            const response = await $authHost.get('/apig/reestr-v3', {
                params: {
                    project_id: projectId,
                    object_type: 'transformers',
                    is_active: 'true'
                }
            });

            if (response.data.success) {
                const objectsInProjects = response.data.data
                    .filter(obj => obj.object_id)
                    .map(obj => obj.object_id);
                setObjectsIntProject(objectsInProjects);
            }
        } catch (error) {
            console.error('Error checking object in projects:', error);
            setObjectsIntProject([]);
        } finally {
            setIsCheckingProjects(false);
        }
    }, [objectData?.id]);

    // Проверяем объект в проектах при изменении объекта
    useEffect(() => {
        console.log(formData)
        if (formData.object?.fid) {
            checkObjectInProjects(formData.object.fid);
        } else {
            setProjectsWithObject([]);
        }
    }, [formData.object, checkObjectInProjects]);

    useEffect(() => {
        console.log(formData)
        if (formData.project?.id) {
            getObjecstInProject(formData.project.id);
        } else {
            setObjectsIntProject([]);
        }
    }, [formData.project?.id, getObjecstInProject]);

    // Получаем информацию о заблокированных проектах (исключая текущий проект при редактировании)
    const blockedProjects = (projects || []).filter(project =>
        projectsWithObject.includes(project.id) &&
        !(isEditing && objectData?.project_id === project.id)
    );

    // Создаем список проектов с заблокированными вверху
    const sortedProjects = [
        ...blockedProjects, // заблокированные проекты вверху
        ...(projects || []).filter(project =>
            !projectsWithObject.includes(project.id) ||
            (isEditing && objectData?.project_id === project.id)
        ) // доступные проекты + текущий проект при редактировании
    ];

    console.log(projectsWithObject)

    // Заполняем форму данными объекта при редактировании
    useEffect(() => {
        if (isEditing && objectData && open) {
            console.log(objectData)

            // Новая структура данных: data.categories
            const categories = objectData.data?.categories || [];

            // Преобразуем categories в структуру для формы с сохранением works
            const categoriesData = categories.map(cat => {
                const equipItem = equipment.find(eq => Number(eq.id) === Number(cat.id));
                if (!equipItem) return null;

                // Обработка responsible
                // let responsibleObj = null;
                // if (cat.responsible) {
                //     const respId = typeof cat.responsible === 'string' ? parseInt(cat.responsible) : cat.responsible;
                //     responsibleObj = responsible.find(r => r.id === respId) || null;
                // }

                // Обработка coexecutors
                // let coexecutorsList = [];
                // if (cat.coexecutors) {
                //     let coexecIds = [];
                //     if (Array.isArray(cat.coexecutors)) {
                //         coexecIds = cat.coexecutors;
                //     } else if (typeof cat.coexecutors === 'string') {
                //         try {
                //             coexecIds = JSON.parse(cat.coexecutors);
                //             if (!Array.isArray(coexecIds)) {
                //                 coexecIds = [coexecIds];
                //             }
                //         } catch (e) {
                //             const parsed = parseInt(cat.coexecutors);
                //             if (!isNaN(parsed)) {
                //                 coexecIds = [parsed];
                //             }
                //         }
                //     } else {
                //         coexecIds = [cat.coexecutors];
                //     }
                //     coexecutorsList = coexecIds.map(id => {
                //         const coexecId = typeof id === 'string' ? parseInt(id) : id;
                //         return responsible.find(r => r.id === coexecId);
                //     }).filter(Boolean);
                // }

                return {
                    id: cat.id,
                    uuid: cat.uuid || crypto.randomUUID(),
                    equipment: equipItem,
                    plan_start: cat.plan_start || null,
                    fact_start: cat.fact_start || null,
                    plan_end: cat.plan_end || null,
                    fact_end: cat.fact_end || null,
                    stage: cat.stage || null,
                    responsible:  cat.responsible, //responsibleObj,
                    coexecutors:  cat.coexecutors, //coexecutorsList,
                    closing: cat.closing || null,
                    cost: cat.cost || null,
                    comment: cat.comment || null,
                    name_by_acts: cat.name_by_acts || null,
                    works: cat.works,
                    // (cat.works || []).map(work => {
                    //     // Обработка responsible для работы
                    //     let workResponsible = null;
                    //     if (work.responsible) {
                    //         const respId = typeof work.responsible === 'string' ? parseInt(work.responsible) : work.responsible;
                    //         workResponsible = responsible.find(r => r.id === respId) || null;
                    //     }

                    //     // Обработка coexecutors для работы
                    //     let workCoexecutors = [];
                    //     if (work.coexecutors) {
                    //         let coexecIds = [];
                    //         if (Array.isArray(work.coexecutors)) {
                    //             coexecIds = work.coexecutors;
                    //         } else if (typeof work.coexecutors === 'string') {
                    //             try {
                    //                 coexecIds = JSON.parse(work.coexecutors);
                    //                 if (!Array.isArray(coexecIds)) {
                    //                     coexecIds = [coexecIds];
                    //                 }
                    //             } catch (e) {
                    //                 const parsed = parseInt(work.coexecutors);
                    //                 if (!isNaN(parsed)) {
                    //                     coexecIds = [parsed];
                    //                 }
                    //             }
                    //         } else {
                    //             coexecIds = [work.coexecutors];
                    //         }
                    //         workCoexecutors = coexecIds.map(id => {
                    //             const coexecId = typeof id === 'string' ? parseInt(id) : id;
                    //             return responsible.find(r => r.id === coexecId);
                    //         }).filter(Boolean);
                    //     }

                    //     return {
                    //         ...work,
                    //         responsible: workResponsible,
                    //         coexecutors: workCoexecutors
                    //     };
                    // })
                };
            }).filter(Boolean);

            setFormData({
                object: objectData.object || null,
                project: projects.find(p => p.id === objectData.project_id) || projects.find(p => p.id === selectedProjectId) || null,
                categories: categoriesData,
                creator: objectData.creator || 'Неизвестно',
                modifier: currentUser ? {
                    id: currentUser.id,
                    name: currentUser.display_name
                } : undefined,
            });

            // Инициализируем раскрытые категории для категорий с работами
            const categoriesWithWorks = categoriesData.filter(cat => cat.works && cat.works.length > 0);
            if (categoriesWithWorks.length > 0) {
                setExpandedCategories(new Set(categoriesWithWorks.map(cat => cat.id)));
            } else {
                setExpandedCategories(new Set());
            }

            // Закрываем меню работ при загрузке данных
            setWorkMenuAnchor(null);
            setSelectedCategoryForWork(null);
        } else if (!isEditing && open) {
            // Очищаем форму для создания нового объекта
            setFormData({
                object: null,
                project: projects.find(p => p.id === selectedProjectId) || null,
                categories: [],
                creator: currentUser ? {
                    id: currentUser.id,
                    name: currentUser.display_name
                } : undefined,
                modifier: currentUser ? {
                    id: currentUser.id,
                    name: currentUser.display_name
                } : undefined,
            });
            // Сбрасываем раскрытые категории и меню работ
            setExpandedCategories(new Set());
            setWorkMenuAnchor(null);
            setSelectedCategoryForWork(null);
        }
    }, [objectData, open, isEditing, selectedProjectId, equipment, responsible, projects]);

    // Устанавливаем проект по умолчанию при открытии диалога
    // useEffect(() => {
    //     if (open && !isEditing && selectedProjectId && projects.length > 0) {
    //         const defaultProject = projects.find(p => p.id === selectedProjectId);
    //         if (defaultProject) {
    //             setFormData(prev => ({
    //                 ...prev,
    //                 project: defaultProject
    //             }));
    //         }
    //     }
    // }, [open, isEditing, selectedProjectId, projects]);

    // Сбрасываем проект, если он стал недоступным (но не при редактировании текущего проекта)
    useEffect(() => {
        if (formData.project && blockedProjects.some(p => p.id === formData.project.id)) {
            setFormData(prev => ({
                ...prev,
                project: null
            }));
        }
    }, [blockedProjects, formData.project]);



    const handleSave = async () => {
        // Проверяем, что выбранный проект не заблокирован
        if (formData.project && blockedProjects.some(p => p.id === formData.project.id)) {
            alert('Выбранный проект недоступен для данного объекта. Пожалуйста, выберите другой проект.');
            return;
        }

        // Валидация дат

        // Преобразуем структуру формы в структуру data.categories
        if (!formData.categories || formData.categories.length === 0) {
            alert('Необходимо выбрать хотя бы одно оснащение');
            return;
        }
        console.log(formData)
        const categories = formData.categories.map(cat => ({
            id: cat.id,
            uuid: cat.uuid || crypto.randomUUID(),
            plan_start: cat.plan_start || undefined,
            fact_start: cat.fact_start || undefined,
            plan_end: cat.plan_end || undefined,
            fact_end: cat.fact_end || undefined,
            stage: cat.stage !== null && cat.stage !== undefined ? cat.stage : undefined,
            responsible: cat.responsible || undefined,
            coexecutors: Array.isArray(cat.coexecutors) ? cat.coexecutors : undefined,
            closing: cat.closing !== null && cat.closing !== undefined ? cat.closing : undefined,
            cost: cat.cost !== null && cat.cost !== undefined ? cat.cost : undefined,
            comment: cat.comment || undefined,
            name_by_acts: cat.name_by_acts || undefined,
            works: (cat.works || []).map(work => ({
                id: work.id,
                plan_start: work.plan_start || undefined,
                fact_start: work.fact_start || undefined,
                plan_end: work.plan_end || undefined,
                fact_end: work.fact_end || undefined,
                stage: work.stage !== null && work.stage !== undefined ? work.stage : undefined,
                responsible: work.responsible || undefined,
                coexecutors: Array.isArray(work.coexecutors) ? work.coexecutors : undefined,
                closing: work.closing !== null && work.closing !== undefined ? work.closing : null,
                cost: work.cost !== null && work.cost !== undefined ? work.cost : null,
                comment: work.comment || '',
                name_by_acts: work.name_by_acts || ''
            }))
        }));

        const objectToSave = {
            object_id: formData.object.fid || formData.object.id,
            object_type: formData.object.type,
            project_id: formData.project?.id || null,
            data: {
                categories: categories
            },
            creator: formData.creator?.id || formData.creator,
            modifier: formData.modifier?.id || formData.modifier
        };

        console.log('Saving object:', objectToSave);

        try {
            let res;
            if (isEditing) {
                res = await $authHost.put(`/apig/reestr-v3/${objectData.id}`, objectToSave);
            } else {
                res = await $authHost.post(`/apig/reestr-v3`, objectToSave);
            }

            console.log(isEditing ? 'Updating object:' : 'Creating object:', objectToSave);

            if (res?.data?.success) {
                onClose(true);
            } else {
                alert('Ошибка при сохранении: ' + (res?.data?.error || 'Неизвестная ошибка'));
            }
        } catch (error) {
            console.error('Error saving object:', error);
            alert('Ошибка при сохранении: ' + (error.message || 'Неизвестная ошибка'));
        }
    };

    const handleCancel = () => {
        onClose();
    };

    const dialogTitle = isEditing ? 'Редактирование объекта' : 'Создание нового объекта';
    const buttonText = isEditing ? 'Сохранить изменения' : 'Создать объект';
    const buttonColor = isEditing ? '#1976d2' : '#7cb342';
    const IconComponent = isEditing ? EditIcon : AddIcon;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xl"
            fullWidth
            fullScreen={isMobile}
            sx={{ zIndex: 5100 }}
            slotProps={{
                paper: {
                    sx: {
                        bgcolor: darkMode ? '#1e1e1e' : '#f5f7fa',
                        color: darkMode ? '#f5f7fa' : '#333',
                        borderRadius: isMobile ? 0 : 3,
                        boxShadow: darkMode
                            ? '0 8px 32px rgba(0, 0, 0, 0.4)'
                            : '0 8px 32px rgba(0, 0, 0, 0.12)',
                        animation: 'slideIn 0.3s ease-out',
                        position: 'relative',
                        zIndex: 1301,
                        '@keyframes slideIn': {
                            from: {
                                opacity: 0,
                                transform: 'scale(0.95)',
                            },
                            to: {
                                opacity: 1,
                                transform: 'scale(1)',
                            },
                        },
                    }
                }
            }}
        >
            <DialogTitle sx={{
                bgcolor: darkMode ? '#2b2b2b' : '#fff',
                borderBottom: darkMode
                    ? '1px solid rgba(255, 255, 255, 0.12)'
                    : '1px solid rgba(0, 0, 0, 0.12)',
                py: 1,
                px: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 36,
                        height: 36,
                        borderRadius: '50%',
                        bgcolor: `${buttonColor}20`,
                        border: `2px solid ${buttonColor}`
                    }}>
                        <IconComponent sx={{
                            fontSize: '24px',
                            color: buttonColor
                        }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{
                            fontWeight: 700,
                            mb: 0.3,
                            color: darkMode ? '#f5f7fa' : '#333'
                        }}>
                            {dialogTitle}
                        </Typography>
                    </Box>
                </Box>

                <IconButton
                    onClick={handleCancel}
                    size="large"
                    sx={{
                        color: darkMode ? '#f5f7fa' : '#666',
                        '&:hover': {
                            bgcolor: 'rgba(244, 67, 54, 0.1)',
                            color: '#f44336'
                        }
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{
                bgcolor: darkMode ? '#1e1e1e' : '#f5f7fa',
                p: 3,
                position: 'relative',
                zIndex: 1302
            }}>
                <ObjectForm
                    formData={formData}
                    darkMode={darkMode}
                    isEditing={isEditing}
                    setFormData={setFormData}
                    objects={objects}
                    stages={stages}
                    responsible={responsible}
                    equipment={equipment}
                    projects={projects}
                    selectedProjectId={selectedProjectId}
                    sortedProjects={sortedProjects}
                    blockedProjects={blockedProjects}
                    isCheckingProjects={isCheckingProjects}
                    expandedCategories={expandedCategories}
                    setExpandedCategories={setExpandedCategories}
                    workMenuAnchor={workMenuAnchor}
                    setWorkMenuAnchor={setWorkMenuAnchor}
                    selectedCategoryForWork={selectedCategoryForWork}
                    setSelectedCategoryForWork={setSelectedCategoryForWork}
                    handleAddCategoryClick={handleAddCategoryClick}
                    categoryMenuAnchor={categoryMenuAnchor}
                />
            </DialogContent>

            {/* Popper для выбора категории оснащения */}
            {categoryMenuAnchor && (
                <Popper
                    open={Boolean(categoryMenuAnchor)}
                    anchorEl={categoryMenuAnchor}
                    placement="bottom-start"
                    style={{ zIndex: 13000, width: categoryMenuAnchor?.offsetWidth || 300 }}
                >
                    <Autocomplete
                        open={true}
                        onClose={handleCategoryMenuClose}
                        options={equipment}
                        getOptionLabel={(option) => option.name}
                        onChange={(event, newValue) => {
                            if (newValue) {
                                handleAddCategory(newValue.id);
                            }
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                size="small"
                                autoFocus
                                sx={{
                                    bgcolor: darkMode ? '#2b2b2b' : '#fff',
                                    '& .MuiOutlinedInput-root': {
                                        color: darkMode ? '#f5f7fa' : '#333',
                                        '& fieldset': {
                                            borderColor: darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: darkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: darkMode ? '#64b5f6' : '#1976d2',
                                        },
                                    },
                                }}
                            />
                        )}
                        PaperComponent={({ children, ...other }) => (
                            <Paper
                                {...other}
                                sx={{
                                    bgcolor: darkMode ? '#2b2b2b' : '#fff',
                                    border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.12)',
                                    '& .MuiAutocomplete-option': {
                                        color: darkMode ? '#f5f7fa' : '#333',
                                        '&:hover': {
                                            bgcolor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)',
                                        },
                                        '&[aria-selected="true"]': {
                                            bgcolor: darkMode ? 'rgba(100, 181, 246, 0.2)' : 'rgba(25, 118, 210, 0.1)',
                                        },
                                    },
                                }}
                            >
                                {children}
                            </Paper>
                        )}
                        filterOptions={(options, { inputValue }) => {
                            if (!inputValue) return options;

                            const searchTerms = inputValue.toLowerCase().split(' ').filter(term => term.length > 0);

                            return options.filter(option => {
                                const optionText = option.name.toLowerCase();
                                return searchTerms.every(term => optionText.includes(term));
                            });
                        }}
                    />
                </Popper>
            )}

            {/* Меню выбора работы для добавления - рендерится на уровне Dialog */}
            <Menu
                anchorEl={workMenuAnchor}
                open={Boolean(workMenuAnchor)}
                onClose={handleWorkMenuClose}
                disablePortal={false}
                PaperProps={{
                    sx: {
                        bgcolor: darkMode ? '#2b2b2b' : '#fff',
                        border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.12)',
                        minWidth: 250,
                        maxHeight: 400,
                        '& .MuiMenuItem-root': {
                            fontSize: '0.875rem',
                            color: darkMode ? '#f5f7fa' : '#333',
                            '&:hover': {
                                bgcolor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
                            },
                            '&.Mui-disabled': {
                                opacity: 0.5,
                            }
                        }
                    }
                }}
                slotProps={{
                    popper: {
                        sx: {
                            zIndex: 14000,
                        },
                        disablePortal: false,
                    },
                    root: {
                        sx: {
                            zIndex: 14000,
                        }
                    }
                }}
                MenuListProps={{
                    sx: {
                        zIndex: 14000,
                    }
                }}
            >
                {selectedCategoryForWork && (() => {
                    const category = formData.categories?.find(cat => cat.uuid === selectedCategoryForWork);
                    if (!category) return null;

                    const existingWorkIds = (category.works || []).map(w => w.id);
                    const availableWorkIds = requirementsTree[String(category.id)] || [];

                    return availableWorkIds.map(workId => {
                        const isDisabled = existingWorkIds.includes(workId);
                        const workName = equipment.find(eq => Number(eq.id) === Number(workId))?.name || `Работа ${workId}`;

                        return (
                            <MenuItem
                                key={workId}
                                onClick={() => {
                                    if (!isDisabled && selectedCategoryForWork) {
                                        const categoryIndex = formData.categories?.findIndex(cat => cat.uuid === selectedCategoryForWork);
                                        if (categoryIndex !== -1) {
                                            setFormData(prev => {
                                                const newCategories = [...(prev.categories || [])];
                                                const existingWorks = newCategories[categoryIndex].works || [];
                                                if (!existingWorks.find(w => w.id === workId)) {
                                                    newCategories[categoryIndex] = {
                                                        ...newCategories[categoryIndex],
                                                        works: [...existingWorks, {
                                                            id: workId,
                                                            plan_start: null,
                                                            fact_start: null,
                                                            plan_end: null,
                                                            fact_end: null,
                                                            stage: null,
                                                            responsible: null,
                                                            coexecutors: null,
                                                            closing: null,
                                                            cost: null,
                                                            comment: null,
                                                            name_by_acts: null
                                                        }]
                                                    };
                                                }
                                                return { ...prev, categories: newCategories };
                                            });
                                        }
                                    }
                                    handleWorkMenuClose();
                                }}
                                disabled={isDisabled}
                            >
                                {workName}
                                {isDisabled && <Typography variant="caption" sx={{ ml: 1, color: darkMode ? '#999' : '#666' }}>(уже добавлена)</Typography>}
                            </MenuItem>
                        );
                    });
                })()}
            </Menu>

            <DialogActions sx={{
                bgcolor: darkMode ? '#2b2b2b' : '#fff',
                borderTop: darkMode
                    ? '1px solid rgba(255, 255, 255, 0.12)'
                    : '1px solid rgba(0, 0, 0, 0.12)',
                py: 1.5,
                px: 3,
                gap: 2,
                justifyContent: 'flex-end'
            }}>
                <Button
                    onClick={handleCancel}
                    variant="outlined"
                    size="large"
                    sx={{
                        color: darkMode ? '#bbb' : '#666',
                        borderColor: darkMode ? '#555' : '#ddd',
                        px: 1,
                        py: 0.3,
                        textTransform: 'none',
                        fontSize: '1rem',
                        fontWeight: 500,
                        borderRadius: 2,
                        '&:hover': {
                            borderColor: darkMode ? '#777' : '#bbb',
                            bgcolor: 'transparent'
                        }
                    }}
                >
                    Отмена
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    size="large"
                    startIcon={<SaveIcon />}
                    disabled={!formData.object || !formData.project || blockedProjects.some(p => p.id === formData.project?.id)}
                    sx={{
                        bgcolor: buttonColor,
                        color: 'white',
                        px: 1,
                        py: 0.3,
                        textTransform: 'none',
                        fontSize: '1rem',
                        fontWeight: 500,
                        borderRadius: 2,
                        '&:hover': {
                            bgcolor: isEditing ? '#1565c0' : '#689f38',
                            transform: 'translateY(-2px)',
                            boxShadow: `0 4px 12px ${buttonColor}66`
                        },
                        '&:disabled': {
                            bgcolor: darkMode ? '#444' : '#ddd',
                            color: darkMode ? '#666' : '#999'
                        }
                    }}
                >
                    {buttonText}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ObjectDialog; 